import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';

import { windowScrollToTop } from '../../../../utils/windowScrollToTop';
import { saveNominationDraft } from '../../../../utils/saveNominationDraft';

import { CommonCollections } from '../../../../App';
import { useProposerCollections } from '../../../../context/ProposerCollections';
import { useLoggedInUserContextProvider } from '../../../../context/LoggedInUserContext';
import { useAdminCollections } from '../../../../context/AdminCollections';
import { useStaticCollections } from '../../../../context/StaticCollections';
import ModalConfirmation from '../../ModalConfirmation';

const ButtonSubmit = ({
  formName = '',
  textContent,
  cssClass = '',
  setFormStatus = null,
  setVisibleNotification = false,
  scrollToTop = false,
  isDisabled = false,
  isDraftButton = false,
  errors = null,
  numberOfErrors = null,
  isHidden = false,
  requireConfirmation = false,
  handleSubmit,
  onSubmit,
  setValue,
  watch,
}) => {
  const { refreshProposerCollections, setRefreshProposerCollections } =
    useProposerCollections();
  const { usersArray } = useStaticCollections();
  const { nominationId, setNominationId, setIsFormSubmitted } =
    useContext(CommonCollections);
  const { userDetails } = useLoggedInUserContextProvider();
  const { refreshNominations, setRefreshNominations } = useAdminCollections();
  const [showModal, setShowModal] = useState(false);

  if (isHidden) {
    return null;
  }

  const handleConfirmedAction = () => {
    if (scrollToTop) {
      windowScrollToTop();

      if (isDraftButton && formName === 'nomination') {
        saveNominationDraft(
          watch ? watch() : null,
          refreshProposerCollections,
          setRefreshProposerCollections,
          userDetails,
          nominationId,
          refreshNominations,
          setRefreshNominations,
          usersArray,
          errors,
          numberOfErrors,
          setNominationId,
        );
      }
    }
    if (setFormStatus) {
      setFormStatus(true);
      setVisibleNotification(true);
    }

    if (!isDraftButton && numberOfErrors === 0 && formName !== 'referees') {
      setValue('nominationStatusTypeId', 2, { shouldDirty: true });
      setIsFormSubmitted(true);
    }
    handleSubmit(onSubmit)();
    setShowModal(false);
  };

  const handleButtonClick = () => {
    if (isDraftButton) {
      const blockingErrorTypes = [
        'expired-nomination',
        'already-elected',
        'deceased-candidate',
        'active-nomination-duplicate',
        'draft-duplicate',
      ];
      const hasBlockingErrors = Object.values(errors || {}).some((error) =>
        blockingErrorTypes.includes(error?.type),
      );

      if (hasBlockingErrors) {
        return;
      }
    }

    if (requireConfirmation) {
      setShowModal(true);
    } else {
      handleConfirmedAction();
    }
  };

  return (
    <>
      <button
        type="button"
        className={cssClass}
        onClick={handleButtonClick}
        disabled={isDisabled}
      >
        {textContent}
      </button>
      <ModalConfirmation
        showModal={showModal}
        setShowModal={setShowModal}
        handleCancelSelect={() => setShowModal(false)}
        bodyText={
          formName === 'nomination'
            ? `Are you sure you want to **submit** the nomination?<br/><br/>
        Once submitted the **referees** will be contacted and you will not be able to make any further edits. To continue editing select **Cancel** and use the **Save Draft** button to save the changes.`
            : `Are you sure you want to **${textContent.toLowerCase()}**?`
        }
        handleConfirmSelect={() => handleConfirmedAction()}
      />
    </>
  );
};

ButtonSubmit.propTypes = {
  formName: PropTypes.string,
  textContent: PropTypes.string,
  cssClass: PropTypes.string,
  setFormStatus: PropTypes.func,
  setVisibleNotification: PropTypes.func,
  scrollToTop: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isDraftButton: PropTypes.bool,
  isHidden: PropTypes.bool,
  errors: PropTypes.object,
  numberOfErrors: PropTypes.number,
  requireConfirmation: PropTypes.bool,
  handleSubmit: PropTypes.func,
  onSubmit: PropTypes.func,
  setValue: PropTypes.func,
  watch: PropTypes.func,
};

export default ButtonSubmit;
